/**
 * Google Tag Manager data layer helpers.
 *
 * GTM container configuration (marketing team):
 *
 * Data Layer Variables:
 * - dlv - product_ids        -> product_ids
 * - dlv - product_names      -> product_names
 * - dlv - application_type   -> application_type
 * - dlv - application_id     -> application_id
 * - dlv - step_name          -> step_name
 *
 * Triggers:
 * - Custom Event: application_submitted
 * - Custom Event: application_submitted_view (backup on /apply/submitted page load)
 * - Custom Event: application_step where product_ids contains impact-checking
 * - Custom Event: select_item where ecommerce.items item_id equals impact-checking
 *
 * GA4:
 * - Map ecommerce.items from application_submitted / begin_checkout events
 * - Register item_id and item_name as event-scoped custom dimensions
 *
 * UAT and production both set NEXT_PUBLIC_GTM_ID in azure-pipelines.yml.
 * Use hostname filters in GTM/GA4 to separate UAT traffic from production.
 */

import type { AnalyticsProduct, ApplicationType } from '@/lib/analytics-product-context'
import { rollupProductFlags } from '@/lib/analytics-product-context'
import { getCampaignContext } from '@/lib/mixpanel'

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>
  }
}

type AttributionProps = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  utm_officer?: string
  landing_page?: string
}

type ProductAwarePayload = {
  application_type?: ApplicationType
  application_id?: string
  products?: AnalyticsProduct[]
  product_ids?: string[]
  product_names?: string[]
}

function getDataLayer(): Array<Record<string, unknown>> | null {
  if (typeof window === 'undefined') {
    return null
  }

  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

function getAttributionProps(): AttributionProps {
  const context = getCampaignContext()
  if (!context) {
    return {}
  }

  const touch = context.last_touch || context.first_touch
  if (!touch) {
    return {}
  }

  return {
    utm_source: touch.utm_source,
    utm_medium: touch.utm_medium,
    utm_campaign: touch.utm_campaign,
    utm_content: touch.utm_content,
    utm_term: touch.utm_term,
    utm_officer: touch.utm_officer,
    landing_page: touch.landing_page,
  }
}

function toEcommerceItems(products: AnalyticsProduct[]) {
  return products.map((product) => ({
    item_id: product.item_id,
    item_name: product.item_name,
    item_category: product.item_category,
    item_variant: product.item_variant,
    quantity: product.quantity,
  }))
}

function buildProductEventPayload(
  payload: ProductAwarePayload,
  options?: { transactionId?: string },
): Record<string, unknown> {
  const rollup = payload.products && payload.products.length > 0
    ? rollupProductFlags(payload.products)
    : null

  const eventPayload: Record<string, unknown> = {
    application_type: payload.application_type,
    application_id: payload.application_id,
    ...(rollup
      ? {
          product_ids: rollup.product_ids,
          product_names: rollup.product_names,
        }
      : {
          product_ids: payload.product_ids,
          product_names: payload.product_names,
        }),
  }

  if (payload.products && payload.products.length > 0) {
    eventPayload.ecommerce = {
      ...(options?.transactionId ? { transaction_id: options.transactionId } : {}),
      items: toEcommerceItems(payload.products),
    }
  }

  return eventPayload
}

export function pushDataLayerEvent(
  event: string,
  payload: Record<string, unknown> = {},
): void {
  const dataLayer = getDataLayer()
  if (!dataLayer) {
    return
  }

  dataLayer.push({
    event,
    ...getAttributionProps(),
    ...payload,
  })
}

export function setApplicationContext(payload: ProductAwarePayload): void {
  pushDataLayerEvent('application_context', buildProductEventPayload(payload))
}

export function pushViewItemList(products: AnalyticsProduct[], listName = 'account_catalog'): void {
  pushDataLayerEvent('view_item_list', {
    item_list_name: listName,
    ecommerce: { items: toEcommerceItems(products) },
  })
}

export function pushSelectItem(
  product: AnalyticsProduct,
  action: 'select' | 'deselect',
): void {
  pushDataLayerEvent('select_item', {
    action,
    ecommerce: { items: toEcommerceItems([product]) },
  })
}

export function pushAddToCart(product: AnalyticsProduct, quantity = 1): void {
  pushDataLayerEvent('add_to_cart', {
    ecommerce: { items: toEcommerceItems([{ ...product, quantity }]) },
  })
}

export function pushRemoveFromCart(product: AnalyticsProduct): void {
  pushDataLayerEvent('remove_from_cart', {
    ecommerce: { items: toEcommerceItems([product]) },
  })
}

export function pushBeginCheckout(payload: ProductAwarePayload): void {
  pushDataLayerEvent('begin_checkout', buildProductEventPayload(payload))
}

export function pushApplicationStarted(payload: ProductAwarePayload): void {
  pushDataLayerEvent('application_started', buildProductEventPayload(payload))
}

export function pushApplicationStep(payload: ProductAwarePayload & {
  step_name: string
  step_number: number
  funnel_name: string
  page: string
}): void {
  const { step_name, step_number, funnel_name, page, ...productPayload } = payload
  pushDataLayerEvent('application_step', {
    step_name,
    step_number,
    funnel_name,
    page,
    ...buildProductEventPayload(productPayload),
  })
}

export function pushApplicationSubmitted(payload: ProductAwarePayload & {
  submitted_at?: string
}): void {
  const { submitted_at, ...productPayload } = payload
  pushDataLayerEvent('application_submitted', {
    submitted_at: submitted_at || new Date().toISOString(),
    ...buildProductEventPayload(productPayload, {
      transactionId: payload.application_id,
    }),
  })
}

export function pushApplicationSubmittedView(payload: ProductAwarePayload & {
  submitted_at?: string
}): void {
  const { submitted_at, ...productPayload } = payload
  pushDataLayerEvent('application_submitted_view', {
    submitted_at: submitted_at || new Date().toISOString(),
    ...buildProductEventPayload(productPayload, {
      transactionId: payload.application_id,
    }),
  })
}
